import { useState } from 'react';
import {
  Card, CardContent, Stack, Button, TextField, MenuItem, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar, LinearProgress, Divider, Box,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ListFilters, matchFilters, EMPTY_FILTERS, type FilterValue } from '../../components/ListFilters';
import { sourcingApi } from '../../api/sourcing.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string) => (s === 'completed' ? 'success' : s === 'cancelled' ? 'default' : s === 'in_progress' ? 'warning' : 'info');

export function ManufacturingOrdersPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [create, setCreate] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [form, setForm] = useState({ manufacturerId: '', productName: '', quantity: 0, rate: 0, expectedDeliveryDate: '', dailyCommitmentQty: 0 });
  const [log, setLog] = useState({ logDate: '', producedQty: 0, handoverQty: 0, remarks: '' });
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });

  const manufacturers = useQuery({ queryKey: ['mfr', 'list'], queryFn: () => sourcingApi.listManufacturers({ limit: 200 }) });
  const orders = useQuery({ queryKey: ['mo', 'list'], queryFn: () => sourcingApi.listOrders({ limit: 200 }) });
  const detail = useQuery({ queryKey: ['mo', detailId], queryFn: () => sourcingApi.getOrder(detailId!), enabled: detailId != null });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['mo'] }); qc.invalidateQueries({ queryKey: ['mfr'] }); };

  const createMut = useMutation({
    mutationFn: () => sourcingApi.createOrder({ ...form, manufacturerId: Number(form.manufacturerId), quantity: Number(form.quantity), rate: Number(form.rate), dailyCommitmentQty: Number(form.dailyCommitmentQty), expectedDeliveryDate: form.expectedDeliveryDate || undefined }),
    onSuccess: () => { setCreate(false); setError(null); setToast('Manufacturing order created'); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const logMut = useMutation({
    mutationFn: () => sourcingApi.addDailyLog(detailId!, { ...log, producedQty: Number(log.producedQty), handoverQty: Number(log.handoverQty) }),
    onSuccess: () => { setLog({ logDate: '', producedQty: 0, handoverQty: 0, remarks: '' }); setToast('Daily commitment logged'); qc.invalidateQueries({ queryKey: ['mo'] }); },
    onError: (e) => setToast(extractApiError(e)),
  });
  const completeMut = useMutation({ mutationFn: () => sourcingApi.completeOrder(detailId!), onSuccess: () => { setToast('Order completed'); invalidate(); }, onError: (e) => setToast(extractApiError(e)) });

  return (
    <>
      <PageHeader title="Manufacturing Orders" subtitle="Manufacturer purchase orders with daily stock-handover commitment."
        action={hasPermission('manufacturing_order.create') ? <Button startIcon={<Add />} variant="contained" onClick={() => setCreate(true)}>New Order</Button> : undefined} />

      <ListFilters value={filters} onChange={setFilters} statusOptions={['draft', 'issued', 'in_progress', 'completed', 'cancelled']}
        show={['search', 'status', 'month', 'dateFrom', 'dateTo']} />

      <Card variant="outlined"><CardContent>
        <Table size="small">
          <TableHead><TableRow><TableCell>MO Code</TableCell><TableCell>Product</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Produced</TableCell><TableCell align="right">Amount</TableCell><TableCell>Expected</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
          <TableBody>
            {(orders.data?.data ?? []).filter((o) => matchFilters(filters, { text: `${o.moCode} ${o.productName ?? ''}`, status: o.status, date: o.expectedDeliveryDate })).map((o) => (
              <TableRow key={o.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(o.id)}>
                <TableCell>{o.moCode}</TableCell>
                <TableCell>{o.productName ?? '—'}</TableCell>
                <TableCell align="right">{o.quantity}</TableCell>
                <TableCell align="right">{o.producedQty}</TableCell>
                <TableCell align="right">{inr(o.totalAmount)}</TableCell>
                <TableCell>{o.expectedDeliveryDate ?? '—'}</TableCell>
                <TableCell><Chip size="small" color={statusColor(o.status)} label={o.status} /></TableCell>
              </TableRow>
            ))}
            {(orders.data?.data.length ?? 0) === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary" py={2} textAlign="center">No manufacturing orders.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Create order */}
      <Dialog open={create} onClose={() => setCreate(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Manufacturing Order</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Stack spacing={1.5} mt={1}>
            <TextField select label="Manufacturer" value={form.manufacturerId} onChange={(e) => setForm((f) => ({ ...f, manufacturerId: e.target.value }))} fullWidth required>
              {(manufacturers.data?.data ?? []).filter((m) => m.status === 'active').map((m) => <MenuItem key={m.id} value={m.id}>{m.name} ({m.manufacturerCode})</MenuItem>)}
            </TextField>
            <TextField label="Product" value={form.productName} onChange={(e) => setForm((f) => ({ ...f, productName: e.target.value }))} fullWidth />
            <Stack direction="row" spacing={1}>
              <TextField label="Quantity" type="number" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: Number(e.target.value) }))} fullWidth />
              <TextField label="Rate" type="number" value={form.rate} onChange={(e) => setForm((f) => ({ ...f, rate: Number(e.target.value) }))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField label="Expected Delivery" type="date" InputLabelProps={{ shrink: true }} value={form.expectedDeliveryDate} onChange={(e) => setForm((f) => ({ ...f, expectedDeliveryDate: e.target.value }))} fullWidth />
              <TextField label="Daily Commitment Qty" type="number" value={form.dailyCommitmentQty} onChange={(e) => setForm((f) => ({ ...f, dailyCommitmentQty: Number(e.target.value) }))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setCreate(false)}>Cancel</Button><Button variant="contained" disabled={!form.manufacturerId || !form.quantity || createMut.isPending} onClick={() => createMut.mutate()}>Create</Button></DialogActions>
      </Dialog>

      {/* Detail + daily commitment */}
      <Dialog open={detailId != null} onClose={() => setDetailId(null)} fullWidth maxWidth="md">
        <DialogTitle>{detail.data?.moCode ?? 'Order'}</DialogTitle>
        <DialogContent>
          {detail.isLoading && <LinearProgress />}
          {detail.data && (
            <>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Typography variant="body2">Qty: <b>{detail.data.quantity}</b></Typography>
                <Typography variant="body2">Produced: <b>{detail.data.producedQty}</b></Typography>
                <Typography variant="body2">Daily commitment: <b>{detail.data.dailyCommitmentQty}</b></Typography>
                <Chip size="small" color={statusColor(detail.data.status)} label={detail.data.status} />
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>Daily Commitment / Production Log</Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell align="right">Committed</TableCell><TableCell align="right">Produced</TableCell><TableCell align="right">Handover</TableCell><TableCell>Remarks</TableCell></TableRow></TableHead>
                <TableBody>
                  {detail.data.dailyLogs.map((l) => (
                    <TableRow key={l.id}><TableCell>{l.logDate}</TableCell><TableCell align="right">{l.committedQty}</TableCell><TableCell align="right">{l.producedQty}</TableCell><TableCell align="right">{l.handoverQty}</TableCell><TableCell>{l.remarks ?? '—'}</TableCell></TableRow>
                  ))}
                  {detail.data.dailyLogs.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="caption" color="text.secondary">No logs yet.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
              {hasPermission('manufacturing_order.update') && !['completed', 'cancelled'].includes(detail.data.status) && (
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField size="small" label="Date" type="date" InputLabelProps={{ shrink: true }} value={log.logDate} onChange={(e) => setLog((l) => ({ ...l, logDate: e.target.value }))} />
                    <TextField size="small" label="Produced" type="number" value={log.producedQty} onChange={(e) => setLog((l) => ({ ...l, producedQty: Number(e.target.value) }))} sx={{ width: 110 }} />
                    <TextField size="small" label="Handover" type="number" value={log.handoverQty} onChange={(e) => setLog((l) => ({ ...l, handoverQty: Number(e.target.value) }))} sx={{ width: 110 }} />
                    <Button variant="outlined" disabled={!log.logDate || logMut.isPending} onClick={() => logMut.mutate()}>Add Log</Button>
                  </Stack>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {detail.data && hasPermission('manufacturing_order.complete') && !['completed', 'cancelled'].includes(detail.data.status) && <Button color="success" onClick={() => completeMut.mutate()}>Mark Completed</Button>}
          <Button onClick={() => setDetailId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
